import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { notification } from 'antd'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { Building } from 'models/Building'
import { useRecoilState } from 'recoil'
import { listPointState } from 'store/buildingStore'
import {
  currentAnnotation,
  currentPovState,
  povPerPointState
} from 'store/krpanoStore'
import viewerLoadingState from 'store/viewerloadingState'
import currentViewerState from 'store/viewerStore'
import useSWR from 'swr'
import {
  Annotation,
  FloorMapElement,
  Point,
  SenceData,
  SenceDataResponse
} from 'types/building'
import { Direction, KrpanoInitProps, Stairway } from 'types/krpano'
import Utils from 'utils'
import Krpano from 'utils/krpano'
import { KrpanoEngine } from 'utils/KrpanoEngine'

export default function useViewerKrpano() {
  const { id } = useParams()
  const { t } = useTranslation()
  const [points] = useRecoilState(listPointState)
  const [searchParams] = useSearchParams()
  const urlH = searchParams.get('hlookat')
  const urlV = searchParams.get('vlookat')
  const urlZ = searchParams.get('fov')
  const isAnnotationDisplay = searchParams.get('is_display')
  const urlSubimage = searchParams.get('subimage')

  const { navigate, getBuiildingType } = useDirectory()
  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)
  const [viewerLoading, setViewerLoading] = useRecoilState(viewerLoadingState)

  const [, setCurrentSelected] = useRecoilState(currentAnnotation)
  const [currentPov, setCurrentPov] = useRecoilState(currentPovState)
  const [, setPovPerPoint] = useRecoilState(povPerPointState)
  const updateUrlTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const currentViewerRef = useRef(currentViewer)
  const pointsRef = useRef(points)

  useEffect(() => {
    currentViewerRef.current = currentViewer
  }, [currentViewer])

  useEffect(() => {
    pointsRef.current = points
  }, [points])

  const [currentHgap, setCurrentHgap] = useState(0)

  // function get all data for create scene
  const { trigger: getSenceData } = useMutation(`v1/points/file-info`)

  const { data: building } = useSWR<Building>(
    {
      url: `/v1/buildings/by-type?id=${id}&building_type=${getBuiildingType()}`
    },
    {
      revalidateOnMount: false
    }
  )

  const { data: mapItems, isValidating: isValidatingMapItems } =
    useSWR<FloorMapElement>(
      currentViewer.selectedFloor
        ? {
            url: 'v1/points',
            params: {
              buildingId: id,
              floorId: currentViewer.selectedFloor
            }
          }
        : null,
      {
        revalidateOnMount: false
      }
    )
  const { trigger, isMutating } = useMutation('v1/annotations')

  // handle redirect to next point
  useEffect(() => {
    const handleRedirect = (item: any, clickh: any, clickv: any) => {
      const ponitSelected = mapItems?.points?.find((point) => point.id === item)

      if (ponitSelected?.image360) {
        const krpano = new KrpanoEngine('viewer')
        Krpano.LoadCheckingViewerMode(() => {
          const h = krpano.hlookat
          const v = krpano.vlookat
          const z = krpano.fov
          const currentPoint =
            currentViewerRef.current.pointDetail ||
            pointsRef.current?.find(
              (p) => p.id === currentViewerRef.current.selectedPoint
            ) ||
            mapItems?.points?.find(
              (p) => p.id === currentViewerRef.current.selectedPoint
            )
          const activeImageId =
            krpano.renderedImageId ||
            currentViewerRef.current.selectedImage ||
            currentPoint?.image360?.id

          const radarAngle = Krpano.GetRadarAngle(
            Number(h),
            currentPoint,
            activeImageId
          )
          const targetH = Krpano.GetTargetH(radarAngle, ponitSelected)

          const urlToUpdate = Utils.buildViewerUrl({
            hlookat: targetH,
            vlookat: v,
            fov: z,
            radar_angle: radarAngle,
            subimage: null
          })

          window.history.pushState({}, '', urlToUpdate.toString())

          const hgapOffset = Krpano.GetTransitionHGapOffset(
            currentPoint,
            activeImageId,
            ponitSelected
          )

          krpano.call(`
            hotspot['arrow-${item}'].getcenter(${clickh},${clickv});
            if(${clickv} GT -50 AND ${clickv} LT +50 AND 1000 GT 120, set(${clickv},0));
            set3dtransition("sphere", ${clickh}, ${clickv}, ${z}, 0, ${hgapOffset}, 0.0, 0.5, easeoutquad);
            `)

          setCurrentPov({
            h: Number(h),
            v: Number(v),
            z: Number(z)
          })
          setCurrentViewer((prev) => {
            return {
              ...prev,
              selectedFloor: ponitSelected.floorId || prev.selectedFloor,
              selectedImage: ponitSelected.image360?.id,
              selectedPoint: ponitSelected.id,
              selectedArea: `${ponitSelected.areaId}`,
              isClickToDirection: true,
              isClickToNextFloor: true,
              isSelectPointInLeftMenu: false
            }
          })
        })
      } else {
        notification.error({
          key: 'redirect',
          message: '画像が設定されていません'
        })
      }
    }

    // Only set the function if it doesn't exist or if dependencies changed
    const depsKey = `${mapItems?.points?.length}-${currentViewer.selectedPoint}-${currentViewer.selectedImage}`
    if (
      !(window as any).handle_redirect ||
      (window as any).handle_redirect.depsKey !== depsKey
    ) {
      ;(window as any).handle_redirect = handleRedirect
      ;(window as any).handle_redirect.depsKey = depsKey
    }

    return () => {
      // Only cleanup if component unmounts, not on every re-render
      if ((window as any).handle_redirect?.depsKey === depsKey) {
        delete (window as any).handle_redirect
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapItems?.points?.length, currentHgap])

  // handle click annotation
  const handleClickAnnotation = () => {
    ;(window as any).handle_click_annotation = (annotationId: string) => {
      !isMutating &&
        trigger(
          {
            method: 'get',
            data: {
              building_id: id,
              floor_id: currentViewer.selectedFloor,
              point_id: currentViewer.selectedPoint
            },
            query: [annotationId]
          },
          {
            onSuccess: (data) => {
              setCurrentSelected(data)
            },
            onError: (error) => {
              setCurrentSelected((prev) => ({
                ...prev,
                isMutating: false
              }))
              if (
                error.response?.data?.error?.key === 'annotation_not_exists'
              ) {
                const krpano = new KrpanoEngine('viewer')
                krpano.call(`remove_annotation(${annotationId})`)
                notification.error({
                  key: 'annotationConflictErr',
                  message: t('translation.deletedAnnotationNotice')
                })
                return
              }
              Utils.handleErrorNavigate(error, navigate)
            }
          }
        )
    }
  }

  // Legacy: Full Page Reload Strategy
  const handleChangeFloorReload = useCallback(
    (stairId: string, targetFloor: string, targetPoint: string) => {
      const embedpano = new KrpanoEngine('viewer')
      if (!building?.floors?.find((item) => item.id === Number(targetFloor))) {
        notification.error({
          key: 'stair_error',
          message: t('building.destinationNotFound')
        })
        embedpano.call(`removehotspot('stair-${stairId}')`)
        return
      }

      const urlToUpdate = Utils.buildViewerUrl({
        floor: targetFloor,
        point: targetPoint,
        area: null,
        hlookat: null,
        vlookat: null,
        fov: null,
        is_click_to_next_floor: 'true',
        subimage: null
      })

      window.location.href = urlToUpdate.toString()
    },
    [building?.floors, t]
  )

  // New: In-Page SPA Transition Strategy
  const handleChangeFloorSpa = useCallback(
    (stairId: string, targetFloor: string, targetPoint: string) => {
      const embedpano = new KrpanoEngine('viewer')
      const targetFloorNum = Number(targetFloor)
      if (!building?.floors?.some((item) => item.id === targetFloorNum)) {
        notification.error({
          key: 'stair_error',
          message: t('building.destinationNotFound')
        })
        embedpano.call(`removehotspot('stair-${stairId}')`)
        return
      }

      const urlToUpdate = Utils.buildViewerUrl({
        floor: targetFloorNum,
        point: targetPoint,
        area: null,
        hlookat: null,
        vlookat: null,
        fov: null,
        subimage: null,
        is_click_to_next_floor: null
      })
      window.history.pushState({}, '', urlToUpdate.toString())

      setCurrentPov(null)
      setCurrentSelected(undefined)

      setCurrentViewer((prev) => ({
        ...prev,
        selectedFloor: targetFloorNum,
        selectedPoint: targetPoint,
        selectedArea: undefined,
        selectedImage: undefined,
        isClickToNextFloor: true,
        isSelectPointInLeftMenu: true,
        isClickToDirection: false,
        isClickFromTourCarousel: false
      }))
    },
    [building?.floors, setCurrentPov, setCurrentSelected, setCurrentViewer, t]
  )

  // handle change floor
  const handleChangeFloor = () => {
    ;(window as any).handle_change_view_floor = (
      stair_id: string,
      targetFloor: string,
      targetPoint: string
    ) => {
      const useSpaTransition = true
      if (useSpaTransition) {
        handleChangeFloorSpa(stair_id, targetFloor, targetPoint)
      } else {
        handleChangeFloorReload(stair_id, targetFloor, targetPoint)
      }
    }
  }

  const addPropertiesToKrpano = useCallback(
    (
      krpano: any,
      annotations?: Annotation[],
      directions?: Direction[],
      stairways?: Stairway[],
      tripodUrl?: string,
      point?: Point,
      imageId?: number
    ) => {
      annotations &&
        Krpano.AddAnnotation(
          krpano,
          annotations,
          isAnnotationDisplay,
          false,
          false
        )
      // add tripod logo

      Krpano.AddTripodLogo(krpano, tripodUrl)

      const targetPoint =
        (point?.subImages?.length ? point : undefined) ||
        (currentViewer?.pointDetail?.subImages?.length
          ? currentViewer.pointDetail
          : undefined) ||
        point ||
        currentViewer?.pointDetail
      const activeImageId =
        imageId || currentViewer?.selectedImage || targetPoint?.image360?.id
      const delta = Krpano.GetSubImageDelta(targetPoint, activeImageId)
      directions && Krpano.AddDirectionArrow(krpano, directions, delta)
      stairways && Krpano.AddStairway(krpano, stairways, delta)
    },
    [
      isAnnotationDisplay,
      currentViewer?.pointDetail,
      currentViewer?.selectedImage
    ]
  )

  const krpanoOnreadyCallback = async (
    krpano: any,
    krpanoInitProps: KrpanoInitProps,
    annotations?: Annotation[],
    directions?: Direction[],
    stairways?: Stairway[],
    tripodUrl?: string,
    pointDetail?: Point,
    selectedImageIdOverride?: number
  ) => {
    const { h, v, z, previewUrl, tilesUrl, multires, imageUrl } =
      krpanoInitProps

    const varString = `layer[set_first_view_button].visible=true`
    const currentH = viewerLoading.firstLoading && urlH ? urlH : h
    const currentV = viewerLoading.firstLoading && urlV ? urlV : v
    const currentZ = viewerLoading.firstLoading && urlZ ? urlZ : z

    krpano.set('var_string', varString)
    krpano.call(
      `loadpano('/krpano/xml-assets/pano_view.xml?v=1.0.1', get(var_string), IGNOREKEEP, BLEND(1))`
    )

    const sceneName = Krpano.getScenceNameByFileId(
      pointDetail?.id || currentViewer.selectedPoint,
      points,
      selectedImageIdOverride || currentViewer.selectedImage
    )

    const sceneString = Krpano.generateSceneXML({
      sceneName,
      tilesUrl: tilesUrl || undefined,
      multires: multires || undefined,
      s3Path: imageUrl,
      thumbnailUrl: previewUrl || ''
    })

    const sceneStringFixed = sceneString.replace(/\r?\n|\r/g, ' ')
    krpano.set('scene_xml', sceneStringFixed)
    krpano.call(`includexmlstring(get(scene_xml), null, null, false)`)

    krpano.call(
      `load_scene(${sceneName},${currentH},${currentV},${currentZ},${!viewerLoading.firstLoading})`
    )

    if (typeof document !== 'undefined' && document.fonts?.ready) {
      try {
        await document.fonts.ready
      } catch {
        // continue
      }
    }

    const targetPoint =
      pointDetail ||
      currentViewer?.pointDetail ||
      points.find((p) => p.id === currentViewer.selectedPoint)
    viewerLoading.firstLoading &&
      addPropertiesToKrpano(
        krpano,
        annotations,
        directions,
        stairways,
        tripodUrl,
        targetPoint,
        selectedImageIdOverride
      )

    setViewerLoading((prev) => ({
      ...prev,
      firstLoading: false
    }))
  }

  const embedpano = (
    krpanoInitProps: KrpanoInitProps,
    annotations?: Annotation[],
    directions?: Direction[],
    stairways?: Stairway[],
    tripodUrl?: string,
    pointDetail?: Point,
    selectedImageIdOverride?: number
  ) => {
    const panoViewer = new KrpanoEngine('viewer')
    const target = document.getElementById('viewer-pano-container')
    let currentSelectedPoint = currentViewer.selectedPoint
    let currentSelectedImage =
      selectedImageIdOverride || currentViewer.selectedImage
    if (pointDetail) {
      if (!selectedImageIdOverride) {
        currentSelectedImage = pointDetail.image360?.id
      }
      currentSelectedPoint = pointDetail.id
    }
    const { imageUrl, previewUrl, multires, tilesUrl, h, v, z } =
      krpanoInitProps

    if (target) {
      // fist time only whne krpano not mount -> init krpano with current point and init properties
      if (!panoViewer.instance) {
        ;(window as any).embedpano({
          id: 'embedpano-viewer',
          xml: null,
          target: 'viewer-pano-container',
          html5: 'auto+webgl',
          bgcolor: '#FFFFFF',
          onready: (krpano: any) => {
            krpanoOnreadyCallback(
              krpano,
              krpanoInitProps,
              annotations,
              directions,
              stairways,
              tripodUrl,
              pointDetail,
              currentSelectedImage
            )
          }
        })
      } else {
        const sceneName = Krpano.getScenceNameByFileId(
          currentSelectedPoint,
          points,
          currentSelectedImage
        )
        const currentSceneName = panoViewer.get('xml.scene')

        if (currentSceneName !== sceneName) {
          if (panoViewer.get(`scene[${sceneName}]`) === null) {
            const sceneString = Krpano.generateSceneXML({
              sceneName,
              tilesUrl: tilesUrl || undefined,
              multires: multires || undefined,
              s3Path: imageUrl,
              thumbnailUrl: previewUrl || ''
            }).replace(/\r?\n|\r/g, ' ')

            panoViewer.set('scene_xml', sceneString)
            panoViewer.call(
              `includexmlstring(get(scene_xml), null, null, false)`
            )
          }
          // load init view
          panoViewer.call(`load_scene(${sceneName},${h},${v},${z})`)
        }
        panoViewer.call(`remove_all_hotspot()`)
        addPropertiesToKrpano(
          panoViewer.instance,
          annotations,
          directions,
          stairways,
          tripodUrl,
          pointDetail,
          selectedImageIdOverride
        )
      }
    }
  }

  const handlePreloadScenes = async () => {
    const embedpano = new KrpanoEngine('viewer')
    const listPointTo = points
      .find((item) => item.id === currentViewer.selectedPoint)
      ?.directions?.map((item) => item.point_to)

    if (!listPointTo?.length) {
      return
    }

    await getSenceData(
      {
        method: 'post',
        data: {
          building_id: Number(id),
          floor_id: Number(currentViewer.selectedFloor),
          point_ids: listPointTo
        }
      },
      {
        onSuccess: (data: SenceDataResponse) => {
          try {
            const currentFileId = points.find(
              (p) => p.id === currentViewer.selectedPoint
            )?.image360?.id

            const items: SenceData[] = []

            data.data.forEach((item) => {
              if (
                embedpano.get(`scene[scene_${item.id}]`) === null &&
                item.id !== currentFileId
              ) {
                items.push(item)
              }
            })
            if (items.length) {
              const screenString = Krpano.generateSceneXMLs(items)
              embedpano.call(
                `includexmlstring(${screenString}, null, null, false)`
              )
            }
          } catch (error) {}
        },
        onError: () => {}
      }
    )
  }

  const isPointInCurrentFloor =
    currentViewer?.selectedPoint &&
    !isValidatingMapItems &&
    mapItems?.points?.some((p) => p.id === currentViewer.selectedPoint)

  // get point detail when select point
  useSWR<Point>(
    currentViewer.selectedFloor &&
      currentViewer.selectedPoint &&
      building &&
      mapItems &&
      isPointInCurrentFloor
      ? {
          url: `v1/points/${currentViewer.selectedPoint}`,
          params: {
            building_id: id,
            floor_id: currentViewer?.selectedFloor
          }
        }
      : null,
    {
      onSuccess: (data) => {
        try {
          const isImageValidForPoint =
            currentViewer.selectedImage === data?.image360?.id ||
            data?.subImages?.some(
              (img) => img.id === currentViewer.selectedImage
            )

          let selectedImageId: number | undefined
          if (viewerLoading.firstLoading && urlSubimage) {
            selectedImageId = +urlSubimage
          } else if (isImageValidForPoint) {
            selectedImageId = currentViewer.selectedImage
          } else {
            selectedImageId = data?.image360?.id
          }

          const selectedImageData =
            data?.subImages?.find((img) => img.id === selectedImageId) ||
            data?.image360

          setCurrentViewer((prev) => {
            return {
              ...prev,
              pointDetail: data,
              selectedFloor:
                data?.floorId ||
                currentViewer.selectedFloor ||
                prev.selectedFloor,
              selectedPoint: data?.id,
              selectedImage: selectedImageId
            }
          })

          const urlToUpdate = new URL(window.location.href)
          urlToUpdate.searchParams.set(
            'floor',
            (data?.floorId || currentViewer.selectedFloor)?.toString() || ''
          )
          urlToUpdate.searchParams.set('point', data?.id || '')
          if (data?.areaId) {
            urlToUpdate.searchParams.set('area', data.areaId.toString())
          }

          const prevFloorId = currentViewer.pointDetail?.floorId
          const isSameFloor = !prevFloorId || prevFloorId === data?.floorId
          const isArrowNavSameFloor =
            currentViewer.isClickToDirection && isSameFloor

          // Only persist view parameters in URL on first load or during same-floor arrow navigation
          if (!viewerLoading.firstLoading && !isArrowNavSameFloor) {
            urlToUpdate.searchParams.delete('hlookat')
            urlToUpdate.searchParams.delete('vlookat')
            urlToUpdate.searchParams.delete('fov')
            urlToUpdate.searchParams.delete('radar_angle')
          }

          if (selectedImageId && selectedImageId !== data?.image360?.id) {
            urlToUpdate.searchParams.set('subimage', selectedImageId.toString())
          } else {
            urlToUpdate.searchParams.delete('subimage')
          }
          window.history.pushState({}, '', urlToUpdate.toString())

          if (selectedImageData) {
            const defaultView =
              selectedImageData.id !== data?.image360?.id
                ? selectedImageData.initView || data?.initView
                : data?.initView

            let pov
            // Case 1: First load -> Read view coordinates from URL if available, fallback to defaultView
            if (viewerLoading.firstLoading) {
              const targetUrlH = urlToUpdate.searchParams.get('hlookat')
              const targetUrlV = urlToUpdate.searchParams.get('vlookat')
              const targetUrlZ = urlToUpdate.searchParams.get('fov')

              if (targetUrlH && targetUrlV && targetUrlZ) {
                pov = { h: +targetUrlH, v: +targetUrlV, z: +targetUrlZ }
              } else {
                pov = defaultView
              }
            }
            // Case 2: Direction arrow navigation
            else if (currentViewer.isClickToDirection) {
              // Same floor: retain current POV; different floor: reset to initView
              if (isSameFloor && currentPov) {
                pov = currentPov
              } else {
                pov = defaultView
              }
            }
            // Case 3: Non-arrow navigation (thumbnail click, menu, floor switch, etc.) -> Reset to initView
            else {
              pov = defaultView
            }

            const initProps = {
              imageUrl: selectedImageData.s3Path,
              previewUrl: selectedImageData.multires
                ? `${selectedImageData.tilesUrl}preview.jpg`
                : selectedImageData.thumbnail,
              hgap: data?.initView?.radarHGap || 0,
              h: pov?.h || 0,
              v: pov?.v || 0,
              z: pov?.z || 100,
              multires: selectedImageData.multires,
              tilesUrl: selectedImageData.tilesUrl
            }

            setCurrentHgap(initProps.hgap)
            const annotationList = data?.annotations?.filter((item) => {
              if (item.imageID) {
                return item.imageID === selectedImageData.id
              }
              return selectedImageData.id === data.image360?.id
            })

            const stairList = data?.stairways?.map((item) => ({
              ...item,
              nextFloorName: building?.floors?.find(
                (floor) => floor.id === +(item.nextFloorId || 0)
              )?.name,
              areaId: undefined
            }))
            const tripodUrl = building?.extraInfo?.tripodImageUrl
            embedpano(
              initProps,
              annotationList,
              data?.directions,
              stairList,
              tripodUrl,
              data,
              selectedImageId
            )
            handlePreloadScenes()
          } else {
            Krpano.UnmountPanoViewer()
          }
        } catch (error) {
          console.error('[ViewerKrpano] Parse pointDetail error:', error)
        }
      }
    }
  )

  handleClickAnnotation()
  handleChangeFloor()

  useEffect(() => {
    ;(window as any).sync_current_pov = (h: number, v: number, z: number) => {
      setCurrentPov({ h, v, z })
      const currentPointDetail = currentViewerRef.current.pointDetail
      const currentId =
        currentPointDetail?.id || currentViewerRef.current.selectedPoint

      if (currentId) {
        setPovPerPoint((prev) => ({
          ...prev,
          [currentId]: { h, v, z }
        }))
      }

      if (updateUrlTimeoutRef.current) {
        clearTimeout(updateUrlTimeoutRef.current)
      }
      updateUrlTimeoutRef.current = setTimeout(() => {
        const krpano = new KrpanoEngine('viewer')
        const selectedPointId = currentViewerRef.current.selectedPoint
        const pointFromList = pointsRef.current?.find(
          (p) => p.id === selectedPointId
        )
        const activePoint =
          currentViewerRef.current.pointDetail?.id === selectedPointId
            ? currentViewerRef.current.pointDetail
            : pointFromList || currentViewerRef.current.pointDetail
        const activeImageId =
          krpano.renderedImageId ||
          currentViewerRef.current.selectedImage ||
          activePoint?.image360?.id
        const radarAngle = Krpano.GetRadarAngle(
          Number(h),
          activePoint,
          activeImageId
        )

        const urlToUpdate = Utils.buildViewerUrl({
          hlookat: h,
          vlookat: v,
          fov: z,
          radar_angle: radarAngle
        })
        window.history.replaceState({}, '', urlToUpdate.toString())
      }, 100)
    }
    return () => {
      if (updateUrlTimeoutRef.current) {
        clearTimeout(updateUrlTimeoutRef.current)
      }
      delete (window as any).sync_current_pov
    }
  }, [setCurrentPov, setPovPerPoint])

  useEffect(() => {
    return () => {
      Krpano.UnmountPanoViewer()
      setViewerLoading({ firstLoading: true })
    }
  }, [setViewerLoading])

  return {
    currentViewer,
    setCurrentViewer,
    currentPov,
    setCurrentHgap,
    embedpano
  }
}
